import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { preferredContactMethodOptions } from '@command-center/admin-ecorp';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  usePageHeader,
} from '@command-center/ui-system';
import {
  Grid2X2,
  List,
  Loader2,
  Mail,
  Pencil,
  Phone,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  UserRound,
} from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import {
  createRecord,
  deleteRecord,
  getRecord,
  listRecords,
  updateRecord,
  type PortalRecord,
} from '../api';
import { display, EmptyState, ErrorNotice, Skeletons, useToast } from '../lib';
import {
  buildContactWriteFields,
  contactEmailHref,
  contactPhoneHref,
  findDuplicateEmail,
  isMainPoc,
  relationItems,
} from './contact-utils';

type ViewMode = 'cards' | 'list';

export function ContactsView({ onContext }: { onContext(value: Record<string, unknown>): void }) {
  const { id } = useParams<{ id: string }>();
  const [items, setItems] = useState<PortalRecord[]>([]);
  const [clients, setClients] = useState<PortalRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [search, setSearch] = useState('');
  const [clientId, setClientId] = useState('');
  const [preferredMethod, setPreferredMethod] = useState('');
  const [mainPocOnly, setMainPocOnly] = useState(false);
  const [mode, setMode] = useState<ViewMode>(
    () => (localStorage.getItem('ecorp-view-contacts') as ViewMode | null) ?? 'cards',
  );
  const [selected, setSelected] = useState<PortalRecord>();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const openCreate = useCallback(() => setCreating(true), []);
  usePageHeader(
    {
      title: id && selected ? display(selected.fields['Contact Name']) || 'Contact' : 'Contacts',
      subtitle:
        id && selected
          ? 'Contact details and client relationships.'
          : 'Individual people associated with ECORP client organizations.',
      actions: [
        {
          id: 'new-contact',
          label: 'New Contact',
          icon: <Plus className="h-4 w-4" aria-hidden />,
          variant: 'primary',
          onClick: openCreate,
        },
      ],
    },
    [id, selected, openCreate],
  );
  const queryKey = `${search}|${clientId}|${preferredMethod}|${mainPocOnly}|${refreshKey}`;

  useEffect(() => {
    void listRecords(
      'clients',
      new URLSearchParams({ pageSize: '100', sortField: 'Client Name', sortDirection: 'asc' }),
    )
      .then((page) => setClients(page.items))
      .catch(() => setClients([]));
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const params = new URLSearchParams({
        pageSize: '100',
        sortField: 'Contact Name',
        sortDirection: 'asc',
      });
      if (search.trim()) params.set('search', search.trim());
      if (clientId) params.set('client', clientId);
      if (preferredMethod) params.set('preferredContactMethod', preferredMethod);
      if (mainPocOnly) params.set('mainPocOnly', 'true');
      setLoading(true);
      setError(undefined);
      void listRecords('contacts', params)
        .then((page) => {
          setItems(page.items);
          onContext({
            resource: 'contacts',
            search,
            clientId,
            preferredMethod,
            mainPocOnly,
            recordCount: page.items.length,
          });
        })
        .catch((cause) =>
          setError(cause instanceof Error ? cause.message : 'Could not load Contacts.'),
        )
        .finally(() => setLoading(false));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [queryKey]);

  useEffect(() => {
    if (!id) return;
    void getRecord('contacts', id)
      .then((payload) => setSelected(payload.item))
      .catch(() => undefined);
  }, [id]);

  function setView(next: ViewMode) {
    setMode(next);
    localStorage.setItem('ecorp-view-contacts', next);
  }
  async function refreshSelected(contactId: string) {
    const payload = await getRecord('contacts', contactId);
    setSelected(payload.item);
    setRefreshKey((value) => value + 1);
  }

  return (
    <section className="space-y-4">
      <Card>
        <CardContent className="flex flex-wrap items-center gap-2 p-3">
          <div className="relative min-w-64 flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Search contacts…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <select
            className="field-control w-52"
            value={clientId}
            onChange={(event) => setClientId(event.target.value)}
            aria-label="Filter by Client"
          >
            <option value="">All Clients</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {display(client.fields['Client Name'])}
              </option>
            ))}
          </select>
          <select
            className="field-control w-52"
            value={preferredMethod}
            onChange={(event) => setPreferredMethod(event.target.value)}
            aria-label="Filter by preferred contact method"
          >
            <option value="">All contact methods</option>
            {preferredContactMethodOptions.map((method) => (
              <option key={method} value={method}>
                {method}
              </option>
            ))}
          </select>
          <label className="inline-flex h-10 items-center gap-2 rounded-md border px-3 text-sm">
            <input
              type="checkbox"
              checked={mainPocOnly}
              onChange={(event) => setMainPocOnly(event.target.checked)}
            />
            Main POC only
          </label>
          <Button
            variant={mode === 'cards' ? 'default' : 'outline'}
            size="icon"
            onClick={() => setView('cards')}
            aria-label="Card view"
          >
            <Grid2X2 className="h-4 w-4" />
          </Button>
          <Button
            variant={mode === 'list' ? 'default' : 'outline'}
            size="icon"
            onClick={() => setView('list')}
            aria-label="List view"
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setRefreshKey((value) => value + 1)}
            aria-label="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </CardContent>
      </Card>
      {error && <ErrorNotice message={error} onRetry={() => setRefreshKey((value) => value + 1)} />}
      {loading ? (
        <Skeletons />
      ) : items.length === 0 ? (
        <EmptyState
          title="No contacts match this view."
          hint={
            search || clientId || preferredMethod || mainPocOnly
              ? 'Try clearing one or more filters.'
              : undefined
          }
        />
      ) : mode === 'cards' ? (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {items.map((item) => (
            <ContactCard key={item.id} item={item} onOpen={() => setSelected(item)} />
          ))}
        </div>
      ) : (
        <ContactTable items={items} onOpen={setSelected} />
      )}
      <ContactDetailDialog
        item={selected}
        open={Boolean(selected) && !editing}
        onClose={() => setSelected(undefined)}
        onEdit={() => setEditing(true)}
        onDeleted={() => {
          setSelected(undefined);
          setRefreshKey((value) => value + 1);
        }}
      />
      <ContactFormDialog
        open={creating}
        clients={clients}
        onClose={() => setCreating(false)}
        onSaved={(item) => {
          setCreating(false);
          void refreshSelected(item.id);
        }}
      />
      <ContactFormDialog
        open={Boolean(selected) && editing}
        contact={selected}
        clients={clients}
        onClose={() => setEditing(false)}
        onSaved={(item) => {
          setEditing(false);
          void refreshSelected(item.id);
        }}
      />
    </section>
  );
}

function ContactCard({ item, onOpen }: { item: PortalRecord; onOpen(): void }) {
  const fields = item.fields;
  const emailHref = contactEmailHref(fields['Email Address']);
  const phoneHref = contactPhoneHref(fields['Phone Number']);
  const client = relationItems(fields['Linked Client'])[0];
  const notes = display(fields.Notes);
  return (
    <Card
      className="cursor-pointer transition hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md"
      onClick={onOpen}
    >
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <UserRound className="h-4 w-4 text-muted-foreground" />
          {display(fields['Contact Name']) || 'Untitled Contact'}
          {isMainPoc(item) && <Badge>Main POC</Badge>}
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          {[display(fields['Title/Position']), client?.label].filter(Boolean).join(' · ') ||
            'No title or Client yet'}
        </p>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {emailHref && (
          <a
            className="flex items-center gap-2 text-primary hover:underline"
            href={emailHref}
            onClick={(event) => event.stopPropagation()}
          >
            <Mail className="h-3.5 w-3.5" />
            {display(fields['Email Address'])}
          </a>
        )}
        {phoneHref && (
          <a
            className="flex items-center gap-2 text-primary hover:underline"
            href={phoneHref}
            onClick={(event) => event.stopPropagation()}
          >
            <Phone className="h-3.5 w-3.5" />
            {display(fields['Phone Number'])}
          </a>
        )}
        {display(fields['Preferred Contact Method']) && (
          <Badge variant="secondary">Prefers {display(fields['Preferred Contact Method'])}</Badge>
        )}
        {notes && (
          <p className="border-t pt-2 text-xs text-muted-foreground">
            {notes.length > 140 ? `${notes.slice(0, 137)}…` : notes}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function ContactTable({
  items,
  onOpen,
}: {
  items: PortalRecord[];
  onOpen(item: PortalRecord): void;
}) {
  return (
    <div className="overflow-x-auto rounded-xl border bg-card">
      <table className="w-full text-sm">
        <thead className="bg-muted/60 text-left">
          <tr>
            {[
              'Contact Name',
              'Client',
              'Title/Position',
              'Email Address',
              'Phone Number',
              'Preferred Contact Method',
              'Main POC',
            ].map((label) => (
              <th key={label} className="whitespace-nowrap px-3 py-2 font-medium">
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const fields = item.fields;
            const client = relationItems(fields['Linked Client'])[0];
            const emailHref = contactEmailHref(fields['Email Address']);
            const phoneHref = contactPhoneHref(fields['Phone Number']);
            return (
              <tr
                key={item.id}
                className="cursor-pointer border-t hover:bg-muted/30"
                onClick={() => onOpen(item)}
              >
                <td className="whitespace-nowrap px-3 py-2 font-medium">
                  {display(fields['Contact Name']) || '—'}
                </td>
                <td className="whitespace-nowrap px-3 py-2">
                  {client ? (
                    <Link
                      className="text-primary hover:underline"
                      to={`/clients/${client.id}`}
                      onClick={(event) => event.stopPropagation()}
                    >
                      {client.label}
                    </Link>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-2">
                  {display(fields['Title/Position']) || '—'}
                </td>
                <td className="whitespace-nowrap px-3 py-2">
                  {emailHref ? (
                    <a
                      className="text-primary hover:underline"
                      href={emailHref}
                      onClick={(event) => event.stopPropagation()}
                    >
                      {display(fields['Email Address'])}
                    </a>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-2">
                  {phoneHref ? (
                    <a
                      className="text-primary hover:underline"
                      href={phoneHref}
                      onClick={(event) => event.stopPropagation()}
                    >
                      {display(fields['Phone Number'])}
                    </a>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-2">
                  {display(fields['Preferred Contact Method']) || '—'}
                </td>
                <td className="px-3 py-2">{isMainPoc(item) ? <Badge>Main POC</Badge> : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function ContactFormDialog({
  open,
  contact,
  initialClientId = '',
  clients,
  onClose,
  onSaved,
}: {
  open: boolean;
  contact?: PortalRecord;
  initialClientId?: string;
  clients: PortalRecord[];
  onClose(): void;
  onSaved(item: PortalRecord): void;
}) {
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [clientId, setClientId] = useState(initialClientId);
  const [duplicate, setDuplicate] = useState<PortalRecord>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  useEffect(() => {
    if (!open) return;
    setDraft(contact?.fields ?? {});
    setClientId(relationItems(contact?.fields['Linked Client'])[0]?.id ?? initialClientId);
    setDuplicate(undefined);
    setError(undefined);
  }, [open, contact, initialClientId]);
  useEffect(() => {
    const email = display(draft['Email Address']).trim();
    if (!open || !contactEmailHref(email)) {
      setDuplicate(undefined);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void listRecords('contacts', new URLSearchParams({ pageSize: '20', search: email }))
        .then((page) => {
          if (!cancelled) setDuplicate(findDuplicateEmail(page.items, email, contact?.id));
        })
        .catch(() => {
          if (!cancelled) setDuplicate(undefined);
        });
    }, 350);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, contact?.id, draft['Email Address']]);
  function set(name: string, value: string) {
    setDraft((current) => ({ ...current, [name]: value }));
  }
  async function save(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    if (!display(draft['Contact Name']).trim()) {
      setError('Contact Name is required.');
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      const fields = buildContactWriteFields(draft, clientId, Boolean(contact));
      const result = contact
        ? await updateRecord('contacts', contact.id, fields)
        : await createRecord('contacts', fields);
      onSaved(result.item);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Contact could not be saved.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value && !busy) onClose();
      }}
    >
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {contact ? `Edit ${display(contact.fields['Contact Name'])}` : 'New Contact'}
          </DialogTitle>
        </DialogHeader>
        <form className="space-y-4" onSubmit={save}>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="field-label">
              Contact Name
              <input
                className="field-control"
                required
                value={display(draft['Contact Name'])}
                onChange={(event) => set('Contact Name', event.target.value)}
              />
            </label>
            <label className="field-label">
              Title/Position
              <input
                className="field-control"
                value={display(draft['Title/Position'])}
                onChange={(event) => set('Title/Position', event.target.value)}
              />
            </label>
            <label className="field-label">
              Linked Client
              <select
                className="field-control"
                value={clientId}
                onChange={(event) => setClientId(event.target.value)}
              >
                <option value="">No linked Client</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {display(client.fields['Client Name'])}
                  </option>
                ))}
              </select>
            </label>
            <label className="field-label">
              Preferred Contact Method
              <select
                className="field-control"
                value={display(draft['Preferred Contact Method'])}
                onChange={(event) => set('Preferred Contact Method', event.target.value)}
              >
                <option value="">—</option>
                {preferredContactMethodOptions.map((method) => (
                  <option key={method} value={method}>
                    {method}
                  </option>
                ))}
              </select>
            </label>
            <label className="field-label">
              Email Address
              <input
                className="field-control"
                type="email"
                value={display(draft['Email Address'])}
                onChange={(event) => set('Email Address', event.target.value)}
              />
            </label>
            <label className="field-label">
              Phone Number
              <input
                className="field-control"
                type="tel"
                value={display(draft['Phone Number'])}
                onChange={(event) => set('Phone Number', event.target.value)}
              />
            </label>
            <label className="field-label">
              Alternate Phone
              <input
                className="field-control"
                type="tel"
                value={display(draft['Alternate Phone'])}
                onChange={(event) => set('Alternate Phone', event.target.value)}
              />
            </label>
            <label className="field-label">
              Address
              <input
                className="field-control"
                value={display(draft.Address)}
                onChange={(event) => set('Address', event.target.value)}
              />
            </label>
            <label className="field-label sm:col-span-2">
              Internal Notes
              <textarea
                className="field-control min-h-24"
                value={display(draft.Notes)}
                onChange={(event) => set('Notes', event.target.value)}
              />
            </label>
          </div>
          {duplicate && (
            <p className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-amber-700 dark:text-amber-400">
              Possible duplicate: {display(duplicate.fields['Contact Name']) || 'another Contact'}{' '}
              already uses this email address.
            </p>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" disabled={busy} onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              {contact ? 'Save Contact' : 'Create Contact'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ContactDetailDialog({
  item,
  open,
  onClose,
  onEdit,
  onDeleted,
}: {
  item?: PortalRecord;
  open: boolean;
  onClose(): void;
  onEdit(): void;
  onDeleted(): void;
}) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const fields = item?.fields ?? {};
  const clients = relationItems(fields['Linked Client']);
  const mainPocClients = relationItems(fields['Clients (Main POC)']);
  const emailHref = contactEmailHref(fields['Email Address']);
  const phoneHref = contactPhoneHref(fields['Phone Number']);
  const alternatePhoneHref = contactPhoneHref(fields['Alternate Phone']);
  async function remove() {
    if (!item || busy) return;
    const warning = mainPocClients.length
      ? `${display(fields['Contact Name']) || 'This Contact'} is the Main POC for ${mainPocClients.map((client) => client.label).join(', ')}. Deleting the Contact will remove those Client relationships. Continue?`
      : `Delete ${display(fields['Contact Name']) || 'this Contact'}? This cannot be undone.`;
    if (!window.confirm(warning)) return;
    setBusy(true);
    try {
      await deleteRecord('contacts', item.id, { confirmMainPoc: mainPocClients.length > 0 });
      toast('Contact deleted.');
      onDeleted();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Contact could not be deleted.', 'error');
    } finally {
      setBusy(false);
    }
  }
  const rows = useMemo(
    () => [
      ['Title/Position', display(fields['Title/Position'])],
      ['Preferred Contact Method', display(fields['Preferred Contact Method'])],
      ['Address', display(fields.Address)],
      ['Alternate Phone', display(fields['Alternate Phone'])],
    ],
    [item],
  );
  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value) onClose();
      }}
    >
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            {display(fields['Contact Name']) || 'Contact'}
            {item && isMainPoc(item) && <Badge>Main POC</Badge>}
          </DialogTitle>
        </DialogHeader>
        {item && (
          <div className="space-y-5">
            <div className="flex flex-wrap gap-2">
              {emailHref && (
                <Button size="sm" asChild>
                  <a href={emailHref}>
                    <Mail className="mr-1.5 h-4 w-4" />
                    Email
                  </a>
                </Button>
              )}
              {phoneHref && (
                <Button size="sm" variant="outline" asChild>
                  <a href={phoneHref}>
                    <Phone className="mr-1.5 h-4 w-4" />
                    Call
                  </a>
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={onEdit}>
                <Pencil className="mr-1.5 h-4 w-4" />
                Edit
              </Button>
              <Button
                size="sm"
                variant="destructive"
                className="ml-auto"
                disabled={busy}
                onClick={() => void remove()}
              >
                <Trash2 className="mr-1.5 h-4 w-4" />
                Delete
              </Button>
            </div>
            <section>
              <h3 className="mb-2 border-b pb-2 font-medium">Contact information</h3>
              <dl className="grid gap-3 sm:grid-cols-2">
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Email Address
                  </dt>
                  <dd className="mt-1 text-sm">
                    {emailHref ? (
                      <a className="text-primary hover:underline" href={emailHref}>
                        {display(fields['Email Address'])}
                      </a>
                    ) : (
                      '—'
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Phone Number
                  </dt>
                  <dd className="mt-1 text-sm">
                    {phoneHref ? (
                      <a className="text-primary hover:underline" href={phoneHref}>
                        {display(fields['Phone Number'])}
                      </a>
                    ) : (
                      '—'
                    )}
                  </dd>
                </div>
                {rows.map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      {label}
                    </dt>
                    <dd className="mt-1 text-sm">
                      {label === 'Alternate Phone' && alternatePhoneHref ? (
                        <a className="text-primary hover:underline" href={alternatePhoneHref}>
                          {value}
                        </a>
                      ) : (
                        value || '—'
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
            <section>
              <h3 className="mb-2 border-b pb-2 font-medium">Client relationships</h3>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Linked Client
                  </p>
                  {clients.length ? (
                    clients.map((client) => (
                      <Link
                        key={client.id}
                        className="mr-3 text-primary hover:underline"
                        to={`/clients/${client.id}`}
                        onClick={onClose}
                      >
                        {client.label}
                      </Link>
                    ))
                  ) : (
                    <p className="mt-1 text-muted-foreground">No linked Client</p>
                  )}
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Main POC for
                  </p>
                  {mainPocClients.length ? (
                    mainPocClients.map((client) => (
                      <Link
                        key={client.id}
                        className="mr-3 text-primary hover:underline"
                        to={`/clients/${client.id}`}
                        onClick={onClose}
                      >
                        {client.label}
                      </Link>
                    ))
                  ) : (
                    <p className="mt-1 text-muted-foreground">
                      Not currently designated as a Main POC
                    </p>
                  )}
                </div>
              </div>
            </section>
            <section>
              <h3 className="mb-2 border-b pb-2 font-medium">Internal Notes</h3>
              <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                {display(fields.Notes) || '—'}
              </p>
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
